import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar } from 'lucide-react';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'master'
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  useEffect(() => {
    console.log('UsersPage mounted');
    console.log('API_URL:', API_URL);
    console.log('Token exists:', !!token);
    loadUsers();
  }, []);

  async function loadUsers() {
    console.log('Loading users...');
    try {
      setLoading(true);
      setError(null);
      
      const url = `${API_URL}/api/users`;
      console.log('Fetching from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Response data:', data);
      
      const usersData = data?.data || data || [];
      console.log('Users data:', usersData);
      
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error('Error loading users:', error);
      setError(error.message);
      setUsers([]);
    } finally {
      setLoading(false);
      console.log('Loading complete');
    }
  }

  function handleViewUser(user) {
    setViewingUser(user);
    setIsEditing(false);
    setFormData({
      username: user.username,
      password: '',
      role: user.role
    });
    setShowModal(true);
  }

  async function handleSaveUser(e) {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const submitData = { ...formData };
      
      if (viewingUser && isEditing && !formData.password) {
        delete submitData.password;
      }

      const url = viewingUser && isEditing 
        ? `${API_URL}/api/users/${viewingUser._id}`
        : `${API_URL}/api/users`;
      
      const method = viewingUser && isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка сохранения');
      }

      await loadUsers();
      setShowModal(false);
      setViewingUser(null);
      setIsEditing(false);
      setFormData({ username: '', password: '', role: 'master' });
    } catch (error) {
      console.error('Ошибка сохранения пользователя:', error);
      alert('Ошибка при сохранении пользователя: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteUser() {
    if (!viewingUser) return;
    if (!window.confirm(`Удалить пользователя "${viewingUser.username}"?`)) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${viewingUser._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка удаления');
      }

      setUsers((prev) => prev.filter((u) => u._id !== viewingUser._id));
      setShowModal(false);
      setViewingUser(null);
      setIsEditing(false);
    } catch (error) {
      console.error('Ошибка удаления пользователя:', error);
      alert('Ошибка при удалении пользователя: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  }

  const filteredUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    const q = search.toLowerCase();
    return users.filter(u =>
      u.username?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const getRoleColor = (role) => {
    const colors = {
      'admin': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      'master': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'manager': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
    };
    return colors[role] || colors['master'];
  };

  const getRoleText = (role) => {
    const roles = {
      'admin': 'Администратор',
      'master': 'Мастер',
      'manager': 'Менеджер'
    };
    return roles[role] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Загрузка данных...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-red-400">Ошибка: {error}</div>
        <button 
          onClick={loadUsers}
          className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded transition-colors"
        >
          Попробовать снова
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold">Пользователи</h1>
          <p className="text-zinc-400 text-sm mt-1">Всего пользователей: {users.length}</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Поиск..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 sm:flex-none bg-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => {
              setViewingUser(null);
              setIsEditing(false);
              setFormData({ username: '', password: '', role: 'master' });
              setShowModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded transition-colors whitespace-nowrap"
          >
            Добавить
          </button>
        </div>
      </div>

      {/* 💻 Таблица для десктопа */}
      <div className="hidden md:block bg-zinc-900 rounded-xl overflow-hidden border border-zinc-800">
        <table className="w-full text-left">
          <thead className="bg-zinc-800/50">
            <tr className="text-zinc-400 text-sm">
              <th className="p-4 font-medium">Пользователь</th>
              <th className="p-4 font-medium">Роль</th>
              <th className="p-4 font-medium">Дата создания</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {filteredUsers.map((user) => (
              <tr 
                key={user._id} 
                className="hover:bg-zinc-800/30 transition-colors cursor-pointer"
                onClick={() => handleViewUser(user)}
              >
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                      <span className="text-blue-400 font-semibold">
                        {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <span className="text-white font-medium">{user.username || 'Без имени'}</span>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm border ${getRoleColor(user.role)}`}>
                    {getRoleText(user.role)}
                  </span>
                </td>
                <td className="p-4 text-zinc-400">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ru-RU') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 📱 Карточки для мобильных */}
      <div className="grid md:hidden gap-3">
        {filteredUsers.map((user) => (
          <div
            key={user._id}
            onClick={() => handleViewUser(user)}
            className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 shadow-sm hover:border-zinc-700 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <span className="text-blue-400 font-bold text-lg">
                    {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-white">{user.username || 'Без имени'}</div>
                  <div className="text-xs text-zinc-500">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ru-RU') : '—'}
                  </div>
                </div>
              </div>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${getRoleColor(user.role)}`}>
                {getRoleText(user.role)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Пустое состояние */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-12 bg-zinc-900 rounded-xl border border-zinc-800">
          <h3 className="text-xl font-semibold text-zinc-100 mb-2">
            {search ? 'Пользователи не найдены' : 'Нет пользователей'}
          </h3>
          <p className="text-zinc-400 mb-6">
            {search ? 'Попробуйте изменить параметры поиска' : 'Добавьте первого пользователя'}
          </p>
        </div>
      )}

      {/* 🔹 Модалка */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl w-full max-w-md border border-zinc-700/50 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                  {viewingUser && !isEditing 
                    ? 'Информация о пользователе' 
                    : viewingUser 
                    ? 'Редактировать пользователя' 
                    : 'Добавить пользователя'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setViewingUser(null);
                    setIsEditing(false);
                  }}
                  className="text-zinc-400 hover:text-zinc-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {viewingUser && !isEditing ? (
                /* 👁️ Режим просмотра */
                <div className="space-y-4">
                  <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-16 w-16 bg-blue-500/10 border border-blue-500/30 rounded-full flex items-center justify-center">
                        <span className="text-blue-400 font-bold text-2xl">
                          {viewingUser.username ? viewingUser.username.charAt(0).toUpperCase() : 'U'}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">{viewingUser.username}</h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRoleColor(viewingUser.role)}`}>
                          {getRoleText(viewingUser.role)}
                        </span>
                      </div>
                    </div>

                    {viewingUser.createdAt && (
                      <div className="flex items-center gap-2 pt-3 border-t border-zinc-700">
                        <Calendar className="w-4 h-4 text-zinc-400" />
                        <div>
                          <div className="text-xs text-zinc-500">Создан</div>
                          <div className="text-white">
                            {new Date(viewingUser.createdAt).toLocaleDateString('ru-RU')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleDeleteUser}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                      disabled={submitting}
                    >
                      {submitting ? 'Удаление...' : 'Удалить'}
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                      disabled={submitting}
                    >
                      Изменить
                    </button>
                  </div>
                </div>
              ) : (
                /* ✏️ Режим редактирования / создания */
                <form onSubmit={handleSaveUser} className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm text-zinc-400">Имя пользователя *</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      required
                      placeholder="Введите имя пользователя"
                      className="w-full p-3 rounded-lg bg-zinc-800 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm text-zinc-400">
                      Пароль {viewingUser && isEditing ? '(оставьте пустым, чтобы не менять)' : '*'}
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required={!viewingUser}
                      placeholder="Введите пароль"
                      className="w-full p-3 rounded-lg bg-zinc-800 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm text-zinc-400">Роль *</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      className="w-full p-3 rounded-lg bg-zinc-800 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    >
                      <option value="master">Мастер</option>
                      <option value="admin">Администратор</option>
                      <option value="manager">Менеджер</option>
                    </select>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (viewingUser) {
                          setIsEditing(false);
                        } else {
                          setShowModal(false);
                        }
                      }}
                      className="flex-1 px-5 py-2.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 transition-colors"
                      disabled={submitting}
                    >
                      Назад
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-5 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50"
                      disabled={submitting}
                    >
                      {submitting ? 'Сохранение...' : (viewingUser ? 'Сохранить' : 'Создать')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}